import { useNavigate } from 'react-router-dom';
import { DropdownMenu, DropdownMenuItem } from '../../../shared/components';
import Tooltip from '../../../components/toolTip';
import customIcons from '../../../utils/icons';
import useAuth from '../../../utils/auth';
import { householdDataViewerRoles } from '../../../types/enums';
import { SelectedFilters } from '../../../types/filters.type';
import {
  useCampaignsEnabled, useSendToCalculatorEnabled,
} from '../../../components/organisation-settings/organisationSettingsProvider';
import { hasCreateCampaignAccess, hasSendToCalculatorAccess } from '../../../utils/authRoleWrap';
import PostTemporaryCsv from '../../../services/adapters/temporaryCsv';
import downloadFromUrl from '../../../utils/downloadFromUrl';
import sendToCalculator from '../actions/sendToCalculator';
import { lockedFiltersState } from '../../campaigns';
import { HmrcDataIndicator, useHmrcDataToggle } from '../../hmrc-data-toggle';

// "Take Action" menu for the households matching the current Street View filters.
// Items are hidden when the user's role or the organisation's settings don't allow them.
export default function TakeActionMenu({
  filters, onAction, shouldLockCampaignFilters = false, label = 'Actions',
}: Readonly<{
  filters: SelectedFilters,
  onAction?: () => void, // runs when any item is picked, before its own action
  shouldLockCampaignFilters?: boolean, // the campaign page then shows the filters read-only
  label?: string,
}>) {
  const authUser = useAuth();
  const navigate = useNavigate();
  const { campaignsEnabled } = useCampaignsEnabled();
  const { sendToCalculatorEnabled } = useSendToCalculatorEnabled();
  const hmrcData = useHmrcDataToggle();
  const hasHouseholdLevelAccess = householdDataViewerRoles.includes(authUser.role);

  const items: DropdownMenuItem[] = [];
  if (hasSendToCalculatorAccess(authUser.role, sendToCalculatorEnabled)) {
    items.push({
      id: 'send-to-calculator',
      label: 'Send to Calculator',
      icon: customIcons.calculatorIcon,
      onAction: () => sendToCalculator(filters),
    });
  }
  if (hasCreateCampaignAccess(authUser.role, campaignsEnabled)) {
    items.push({
      id: 'create-campaign',
      label: 'Create new campaign',
      icon: customIcons.sendIcon,
      onAction: () => navigate('/campaigns/new', shouldLockCampaignFilters ? { state: lockedFiltersState } : undefined),
    });
  }
  items.push({
    id: 'export-csv',
    label: 'Export CSV',
    icon: customIcons.downloadIcon,
    onAction: () => downloadFromUrl(
      () => new PostTemporaryCsv().adaptResponse({ selectedFilters: filters }),
      'Preparing your CSV...',
    ),
  });

  const menuItems = onAction
    ? items.map((item) => ({
      ...item,
      onAction: () => {
        onAction();
        return item.onAction();
      },
    }))
    : items;

  return (
    <div className="inline-flex items-center gap-1">
      <DropdownMenu
        label={label}
        icon={customIcons.adjustmentsPlusIcon}
        items={menuItems}
        isDisabled={!hasHouseholdLevelAccess}
        footer={hmrcData.isVisible ? <HmrcDataIndicator isOn={hmrcData.isOn} /> : undefined}
      />
      {!hasHouseholdLevelAccess && (
        <Tooltip label="Why actions are unavailable" text="You do not have access to these actions" />
      )}
    </div>
  );
}
